import React from 'react';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import { useI18n } from '#src/hooks/i18n';
import { useFilters } from '#src/hooks/use-filters';
import { StateMachineContext } from '#src/state-machine/context';
import {
  getMultipleYearsLimit,
  getYearModes,
  getFiltersLimits,
} from '#src/state-machine/selectors';
import { type ViewType } from '#src/types';
import { NestedDropdownMenu } from './filters/nested-dropdown-menu';
import { YearsDropdown } from './filters/years-dropdown';

export default function Header() {
  const { t } = useI18n();
  const {
    settings,
    activeDataset,
    datasetOptions,
    handleDatasetChange,
    levels,
    activeLevel,
    levelCompare,
    handleLevelChange,
    yearsMode,
    years,
    selectedYears,
    handleYearsModeChange,
    handleViewChange,
    activeView,
  } = useFilters();

  const filtersLimits = StateMachineContext.useSelector(getFiltersLimits);
  const multipleYearsLimit = StateMachineContext.useSelector(
    getMultipleYearsLimit
  );
  const yearModes = StateMachineContext.useSelector(getYearModes);

  let viewTypeOptions = ['map', 'chart', 'table'];
  if (settings.allowed_view_types.selectedValues.length > 0) {
    viewTypeOptions = settings.allowed_view_types.selectedValues;
  }

  const VIEW_TYPE_OPTIONS = viewTypeOptions.map((viewType) => ({
    id: viewType,
    name: t(`header.view.${viewType}`),
  }));

  return (
    <div className='bg-edge2edge bg-secondary text-white'>
      <Container className='p-3 p-lg-5'>
        <div className='d-flex align-items-stretch flex-grow-1 d-lg-flex flex-column flex-lg-row filters-container'>
          {Boolean(settings.dataset.showFilter) && (
            <Dropdown
              className='dataset-dropdown mr-lg-3 mb-3 mb-lg-0 flex-grow-1 w-100'
              onSelect={(value, event) => {
                event.preventDefault();
                if (!value) return;
                handleDatasetChange(value);
              }}
            >
              <Dropdown.Toggle
                variant='light'
                className='text-start w-100 d-flex justify-content-between align-items-center'
                style={{ minHeight: '58px' }}
              >
                <p>
                  <span className='text-secondary mr-2'>
                    {t('header.dataset.show-label')}
                  </span>
                  <span className='fw-bold'>{activeDataset?.name}</span>
                </p>
              </Dropdown.Toggle>
              <Dropdown.Menu className='w-100'>
                {datasetOptions.map((opt) => (
                  <Dropdown.Item as='button' key={opt.id} eventKey={opt.id}>
                    {opt.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}

          {Boolean(settings.levelValue.showFilter) && (
            <NestedDropdownMenu
              label={t('header.level.label')}
              parentId={activeLevel?.id}
              selection={levelCompare}
              limits={filtersLimits.level}
              onChange={(newState) => {
                handleLevelChange(newState.parentId, newState.selection);
              }}
              options={levels}
              className='mr-lg-3 mb-3 mb-lg-0 flex-grow-1 w-100 levels-nested-dropdown-menu'
            />
          )}

          <YearsDropdown
            yearsMode={yearsMode}
            selectedYears={selectedYears}
            yearsOptions={years}
            modes={yearModes}
            multipleYearsLimit={multipleYearsLimit}
            onChange={handleYearsModeChange}
            className='mr-lg-3 mb-3 mb-lg-0 flex-grow-1 w-100'
          />
          {viewTypeOptions.length > 1 && (
            <div className='d-flex align-items-center w-100'>
              <Nav
                onSelect={(value, event) => {
                  event.preventDefault();
                  if (!value) return;
                  handleViewChange(value as ViewType);
                }}
                variant='pills'
                activeKey={activeView}
                className='filters-nav-pill view-type-tabs rounded-sm w-100 flex-nowrap flex-lg-wrap'
              >
                {VIEW_TYPE_OPTIONS.map((viewType) => (
                  <Nav.Item className='flex-fill' key={viewType.id}>
                    <Nav.Link
                      eventKey={viewType.id}
                      className='px-2 px-lg-4 d-flex align-items-center justify-content-center text-nowrap'
                    >
                      {viewType.name}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
